import * as THREE from "three";
import { MATS } from "./materials.js";
import { BOUNDS, CREEK, GRADE, Z, creekCut, creekZ, groundY, kerbZ,
         walkFarZ } from "./spec.js";

/**
 * The ground itself: one heightfield sampling spec.groundY over the whole map.
 *
 * It used to be two flat slabs with a retaining wall holding the street up
 * over the gardens and a flight of steps down it in each side passage.  The
 * block now RAMPS instead (spec.terraceY), so the wall, its coping, its
 * full-width collider and both flights are gone: there is no discontinuity
 * left for them to hold back or for the cat to be snapped across.  The one
 * thing the wall also did -- stop daylight showing under the houses, whose
 * back walls stood on it -- is now the houses' own problem, and houses.js and
 * hero/shell.js carry their plinths down to meet the fallen ground.
 *
 * Nothing here is a collider.  The player floors against groundY directly
 * (player.js) and so does reach.mjs, so the mesh only has to LOOK like that
 * function; a collider would be a second, disagreeing copy of the terrain.
 *
 * Individual modules lay their own surfaces (tarmac, paving, borders) on top
 * of this at a few millimetres, placed against their own grade, so nothing
 * here needs to know about them.
 */
// The base plane deliberately overhangs the play area so no edge is visible.
export const allowOversize = true;

// Exactly the footprint the old slabs covered: 4 m of overhang all round.
const X0 = BOUNDS.minX - 4, X1 = BOUNDS.maxX + 4;          // -38 ..  38
const ZBACK = BOUNDS.minZ - 4, ZFRONT = BOUNDS.maxZ + 4;   // -50 ..  37

/**
 * Where the resolution changes.
 *
 * A uniform grid at the density the creek needs -- 0.33 m rows, to resolve a
 * 1.75 m bank -- comes to 40,000 triangles over this map, and nineteen out of
 * twenty of them would land on ground that does not move at all: the street
 * half is level everywhere and the gardens only bend where the creek cuts
 * them.  So the map is meshed in four bands, front to back, each at the
 * density its own shape needs, and costs 3,566.
 *
 * Every band boundary is a line the terrain is LEVEL along and tangent to --
 * the ramp's crest and toe are the smoothstep's flat ends, and the creek band
 * is cut well clear of the channel -- so the neighbouring bands' differing
 * column spacings meet as T-junctions on a straight, shared edge.  A
 * T-junction cracks only when the vertex it lands on is off the edge it lands
 * in; here it never is, and the shading matches too because both sides read
 * their normals from the same function.
 */
const CREST = Z.houseFront;        //   1.5, top of the ramp
const TOE = GRADE.line;            // -11.6, foot of it
const CREEK_FRONT = -32.5;         // front edge of the refined creek band:
// the channel's rim never comes north of -35.4, so this line is flat lawn.

const FLAT = 6;                    // cell size where the ground does not move
const RAMP_ROWS = 15;              // 0.87 m in z: the smoothstep's worst
// chord error is then under 4 mm, and the ramp does not vary in x at all, so
// its columns can be as wide as the flat bands'.
const RAMP_DX = 4;
const CREEK_DX = 1;                // the channel wanders up to 0.51 m in z per
// metre of x, and its cross-section is what the eye reads
const CREEK_R = CREEK.halfWidth + 0.25;   // half-width of the refined strip.
// CREEK.halfWidth is the widest the channel ever gets (pools; riffles pinch
// in), so sizing the strip on it is deliberately generous rather than exact.
const CREEK_FINE = 12;             // rows across the channel: 0.33 m, which
// resolves the 36% bank to about a centimetre
const CREEK_COARSE = 3;            // rows either side, over flat lawn

// The old slabs were 0.02 m boxes, so the ground had an underside; main.js
// clamps the camera pitch to -0.30 rad at up to 5 m of orbit, which puts it
// under the surface whenever the player looks up from below.  A single sheet
// would let them look straight through the world (everything else is
// back-face culled), so the field is emitted twice: once up, once 0.02 m
// lower wound the other way.
const SKIN = 0.02;

export function build(b) {
  terrain(b);
  verge(b);
}

// ---------------------------------------------------------------------------
/**
 * A vertex: position on the surface, and the surface's true normal there.
 *
 * The normal comes from the height function rather than from the triangles,
 * which is what stops the terrain reading as faceted -- neighbouring cells
 * sample the same function at the same point, so their normals agree exactly
 * and the shading runs smoothly across every seam, band boundaries included.
 * A central difference is honest here because groundY is C1 everywhere: the
 * ramp and the channel are both smoothsteps, flat at both of their ends.
 */
const EPS = 0.03;
function vertex(x, z) {
  const dx = (groundY(x + EPS, z) - groundY(x - EPS, z)) / (2 * EPS);
  const dz = (groundY(x, z + EPS) - groundY(x, z - EPS)) / (2 * EPS);
  const s = 1 / Math.hypot(dx, 1, dz);
  return { x, y: groundY(x, z), z, nx: -dx * s, ny: s, nz: -dz * s };
}

/** Deterministic ±1 from a position, for breaking up material boundaries. */
function hash(x, z) {
  const s = Math.sin(x * 12.9898 + z * 78.233) * 43758.5453;
  return 2 * (s - Math.floor(s)) - 1;
}

/**
 * Which surface a triangle is: lawn, the creek's soil bed, or the shingle
 * that collects along the waterline.
 *
 * Read at the triangle's own centre, so every boundary follows a contour of
 * the terrain instead of a straight line across it -- the shingle then widens
 * over the shallows and pinches where the bank steepens, without anyone
 * drawing that.  The thresholds are jittered a couple of centimetres per
 * triangle so the contour breaks up into a shoreline rather than reading as
 * the machined curve it is.
 *
 * The shingle is picked off the WATER PLANE, not off a distance from the
 * centre line: CREEK.waterY is where the water is, so a band of heights
 * around it is exactly the wetted margin however wide the channel runs.
 */
function surfaceAt(x, z) {
  const n = hash(x, z);
  if (creekCut(x, z) < 0.06 + 0.02 * n) return "lawn";
  return Math.abs(groundY(x, z) - CREEK.waterY) < 0.11 + 0.03 * n
    ? "stone" : "soil";
}

/** z stations for a band that does not change shape along x. */
function rows(lo, hi, n) {
  const a = [];
  for (let i = 0; i <= n; i++) a.push(lo + ((hi - lo) * i) / n);
  return a;
}

/**
 * z stations through the creek, following the channel rather than crossing it.
 *
 * The fine rows are pinned to creekZ(x) at a constant offset, which buys the
 * whole reason this band is affordable: the cut depends on the distance to the
 * centre line, so along one of these rows the ground barely changes at all and
 * the columns can be a metre apart.  A rectangular fine band would have had
 * to cover the creek's 6.6 m of wander as well as its own width, and cost two
 * and a half times the triangles for the same silhouette.
 *
 * Every column returns the same number of stations, so the band stays a
 * regular lattice and the rows are continuous curves: no T-junctions inside
 * it, however the creek weaves.  The clamp is a guard -- if the centre line
 * ever wandered out of the band the rows would still come back monotone
 * (flat, wrong terrain) rather than folded over each other.
 */
function creekRows(x) {
  const c = Math.min(Math.max(creekZ(x), ZBACK + CREEK_R + 1),
                     CREEK_FRONT - CREEK_R - 1);
  return [
    ...rows(ZBACK, c - CREEK_R, CREEK_COARSE),
    ...rows(c - CREEK_R, c + CREEK_R, CREEK_FINE).slice(1),
    ...rows(c + CREEK_R, CREEK_FRONT, CREEK_COARSE).slice(1),
  ];
}

function terrain(b) {
  // Three material batches, chosen per triangle.  One mesh would make the
  // creek bed lawn; splitting it by region and merging per material keeps the
  // draw count where it was.
  // Tiles: lawn keeps the old slabs' 1.4; soil is scaled by the ratio of the
  // two materials' own map repeats (20 against 24), so the bank and the grass
  // above it come out at one texel density; the shingle takes 0.55, which is
  // what greenspace.js paints its wet margin along the same water at.
  const out = {
    lawn: { mat: MATS.lawn, tile: 1.4, pos: [], nrm: [] },
    soil: { mat: MATS.soil, tile: 1.17, pos: [], nrm: [] },
    stone: { mat: MATS.stone, tile: 0.55, pos: [], nrm: [] },
  };
  const cols = (dx) => Math.max(1, Math.round((X1 - X0) / dx));

  band(out, cols(CREEK_DX), creekRows);
  band(out, cols(FLAT), () =>
    rows(CREEK_FRONT, TOE, Math.ceil((TOE - CREEK_FRONT) / FLAT)));
  band(out, cols(RAMP_DX), () => rows(TOE, CREST, RAMP_ROWS));
  band(out, cols(FLAT), () =>
    rows(CREST, ZFRONT, Math.ceil((ZFRONT - CREST) / FLAT)));

  for (const m of Object.values(out)) {
    if (!m.pos.length) continue;
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(m.pos, 3));
    g.setAttribute("normal", new THREE.Float32BufferAttribute(m.nrm, 3));
    // ao: 0 because Builder.shade() darkens toward the geometry's own
    // bounding-box floor -- for one mesh spanning the whole map that would
    // paint the creek bed and the whole lower terrace into shadow and leave
    // the street bright.  tint: 0 for the same reason at the other end: the
    // per-piece random tint is meant to break up a row of fence panels, and
    // rolled once for 76 x 87 m of lawn it would simply put the terrain a few
    // percent off every decal the other modules lay on it.
    b.add(g, m.mat, { collider: null, ao: 0, tint: 0, tile: m.tile });
  }
}

/** One band: a regular lattice whose rows may bend with x. */
function band(out, nx, rowsAt) {
  let prev = null;
  for (let i = 0; i <= nx; i++) {
    const x = X0 + ((X1 - X0) * i) / nx;
    const col = rowsAt(x).map((z) => vertex(x, z));
    if (prev) {
      for (let j = 0; j < col.length - 1; j++) {
        tri(out, prev[j], prev[j + 1], col[j]);
        tri(out, prev[j + 1], col[j + 1], col[j]);
      }
    }
    prev = col;
  }
}

function tri(out, a, c, d) {
  const m = out[surfaceAt((a.x + c.x + d.x) / 3, (a.z + c.z + d.z) / 3)];
  for (const v of [a, c, d]) {
    m.pos.push(v.x, v.y, v.z);
    m.nrm.push(v.nx, v.ny, v.nz);
  }
  for (const v of [d, c, a]) {                 // the underside, reverse wound
    m.pos.push(v.x, v.y - SKIN, v.z);
    m.nrm.push(-v.nx, -v.ny, -v.nz);
  }
}

// ---------------------------------------------------------------------------
/**
 * The verge between sidewalk and kerb.
 *
 * It follows the crescent, so the grass strip stays a constant width as the
 * road bends away.  A strip of quads with both edges exactly on the curve
 * functions -- stepped slabs came apart once the crescent ran ~35 deg to the
 * x axis at the level corners.  Sampled at the same 1.5 m stations as
 * road.js's ribbons, so the seam against the sidewalk's far edge is
 * vertex-exact.  Top at 0.013, matching the decal stack documented in
 * road.js; this whole band is on the flat street terrace, so it needs nothing
 * from the heightfield.
 */
function verge(b) {
  const pos = [];
  const n = Math.ceil((BOUNDS.maxX - BOUNDS.minX) / 1.5);
  for (let i = 0; i < n; i++) {
    const xa = BOUNDS.minX + ((BOUNDS.maxX - BOUNDS.minX) * i) / n;
    const xb = BOUNDS.minX + ((BOUNDS.maxX - BOUNDS.minX) * (i + 1)) / n;
    pos.push(
      xa, 0.013, walkFarZ(xa),  xa, 0.013, kerbZ(xa),  xb, 0.013, kerbZ(xb),
      xa, 0.013, walkFarZ(xa),  xb, 0.013, kerbZ(xb),  xb, 0.013, walkFarZ(xb),
    );
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  const nrm = new Float32Array(pos.length);
  for (let i = 1; i < nrm.length; i += 3) nrm[i] = 1;
  g.setAttribute("normal", new THREE.BufferAttribute(nrm, 3));
  b.add(g, MATS.lawnDry, { collider: null, ao: 0, tile: 1.1 });
}
